import { DataGridRef } from '@cimplico/components';
import { useEffect, useRef, useState } from 'react';
import { Workpaper, WorkpaperRoleEnum, WorkpaperStatusEnum } from '../../types/workpaper';
import { useUserContext } from '../../providers/UserProvider';
import { useFirmContext } from '../../providers/FirmProvider';
import { useIsActiveSubscription } from '../../hooks/use-is-active-subscription';
import { useIsFirmRole } from '../../hooks/use-is-firm-role';
import { SubscriptionTier } from '../../types/firm';
import WorkpapersPrimaryActions from './WorkpapersPrimaryActions';
import SecondaryPageActions from '../../components/secondary-page-actions/SecondaryPageActions';
import WorkpapersTable from '../workpapers/WorkpapersTable';
import ArchiveWorkpapersConfirm from '../workpapers/ArchiveWorkpapersConfirm';
import UnarchiveWorkpapersConfirm from '../workpapers/UnarchiveWorkpapersConfirm';
import ChangeWorkpapersVisibilityConfirm from '../workpapers/ChangeWorkpapersVisibilityConfirm';
import UpgradePrompt from '../../components/upgrade-prompt/UpgradePrompt';
import { useSearchParams } from 'react-router-dom';
import { useHasFeatureAccess } from '../../hooks/use-has-feature-access';
import { noRemainingActiveEntities } from '../new-workpaper/utils';
import { Entity } from '../../types/entity';

type WorkpapersSectionProps = {
    groupId: string;
};

function WorkpapersSection({ groupId }: WorkpapersSectionProps): JSX.Element {
    const [search, setSearch] = useState('');
    const [sort, setSort] = useState('');
    const [filter, setFilter] = useState('');
    const [selectedWorkpapers, setSelectedWorkpapers] = useState<Workpaper[]>([]);
    const [secondaryAction, setSecondaryAction] = useState<string | undefined>();
    const { shortId } = useFirmContext();
    const tableRef = useRef<DataGridRef | null>(null);
    const { isAdmin, isExternalUser } = useIsFirmRole();
    const { id: userId } = useUserContext();
    const activeSubscription = useIsActiveSubscription();
    const [query, setQuery] = useSearchParams();
    const firm = useFirmContext();
    const featureAccess = { canTogglePrivate: useHasFeatureAccess('workpaper-toggle-private:write') };

    const canArchive = selectedWorkpapers.filter((wp) => wp.status === WorkpaperStatusEnum.ACTIVE);
    const canUnarchive = selectedWorkpapers.filter(
        (wp) => wp.status === WorkpaperStatusEnum.ARCHIVED && !noRemainingActiveEntities(firm, wp.entity as Entity, firm.subscription)
    );

    const canMakePrivate = selectedWorkpapers.filter((sw) => {
        if (sw.private) {
            return false;
        }
        if (isAdmin || sw.assigned.find((a) => a.id === userId)?.role === WorkpaperRoleEnum.APPROVER) {
            return true;
        }
        return false;
    });

    const canMakePublic = selectedWorkpapers.filter((sw) => {
        if (!sw.private) {
            return false;
        }
        if (isAdmin || sw.assigned.find((a) => a.id === userId)?.role === WorkpaperRoleEnum.APPROVER) {
            return true;
        }
        return false;
    });

    function resetTable(): void {
        setSecondaryAction(undefined);
        setSelectedWorkpapers([]);
        tableRef?.current?.clear();
        tableRef?.current?.resetPagination();
    }

    useEffect(() => {
        if (!query.get('status')) {
            query.append('status', 'ACTIVE');
            setQuery(query);
        }
    }, []);

    return (
        <>
            {secondaryAction === 'UPGRADE' && (
                <UpgradePrompt targetTier={SubscriptionTier.ESSENTIAL} onClose={(): void => setSecondaryAction(undefined)} />
            )}
            {secondaryAction === 'ARCHIVE' && <ArchiveWorkpapersConfirm workpaperIds={canArchive.map((w) => w.id)} onClose={resetTable} />}
            {secondaryAction === 'UNARCHIVE' && (
                <UnarchiveWorkpapersConfirm workpaperIds={canUnarchive.map((w) => w.id)} onClose={resetTable} />
            )}
            {(secondaryAction === 'PRIVATE' || secondaryAction === 'PUBLIC') && (
                <ChangeWorkpapersVisibilityConfirm
                    workpaperIds={secondaryAction === 'PRIVATE' ? canMakePrivate.map((p) => p.id) : canMakePublic.map((p) => p.id)}
                    onClose={resetTable}
                    action={secondaryAction}
                />
            )}
            <div className="flex justify-end items-center pb-4">
                {selectedWorkpapers.length > 0 ? (
                    <SecondaryPageActions maxActionsCount={4}>
                        {canMakePrivate.length > 0 && (
                            <SecondaryPageActions.Button
                                size="sm"
                                label={`Make private (${canMakePrivate.length})`}
                                disabled={!activeSubscription}
                                disabledHint={!activeSubscription ? 'You do not have an active subscription.' : undefined}
                                onClick={(): void => {
                                    if (!featureAccess.canTogglePrivate) {
                                        setSecondaryAction('UPGRADE');
                                    } else {
                                        setSecondaryAction('PRIVATE');
                                    }
                                }}
                            />
                        )}

                        {canMakePublic.length > 0 && (
                            <SecondaryPageActions.Button
                                size="sm"
                                label={`Make public (${canMakePublic.length})`}
                                disabled={!activeSubscription}
                                disabledHint={!activeSubscription ? 'You do not have an active subscription.' : undefined}
                                onClick={(): void => {
                                    if (!featureAccess.canTogglePrivate) {
                                        setSecondaryAction('UPGRADE');
                                    } else {
                                        setSecondaryAction('PUBLIC');
                                    }
                                }}
                            />
                        )}

                        <SecondaryPageActions.Button
                            size="sm"
                            label={`Unarchive (${canUnarchive.length})`}
                            disabled={!activeSubscription || isExternalUser || canUnarchive.length === 0}
                            onClick={(): void => setSecondaryAction('UNARCHIVE')}
                        />
                        <SecondaryPageActions.Button
                            size="sm"
                            type="danger"
                            label={`Archive (${canArchive.length})`}
                            disabled={!activeSubscription || isExternalUser || canArchive.length === 0}
                            onClick={(): void => setSecondaryAction('ARCHIVE')}
                        />
                    </SecondaryPageActions>
                ) : (
                    <WorkpapersPrimaryActions onFilterChange={setFilter} onSearchChange={setSearch} onSortChange={setSort} />
                )}
            </div>
            <WorkpapersTable
                onSelect={setSelectedWorkpapers}
                tableRef={tableRef}
                search={search}
                sort={sort}
                emptyStateNavigateTo={`/workpapers/new?returnTo=/${shortId}/groups/${groupId}?tab=workpapers`}
                filter={`${filter}&group=${groupId}`}
            />
        </>
    );
}

export default WorkpapersSection;
